import { Controller } from "@hotwired/stimulus";
import toast from "../utils/toast";

export default class FormSubmitController extends Controller {
  static targets = ["form", "submit", "spinner", "text", "lockable"];
  static values = { text: String, cancelledMessage: String };

  connect() {
    // Store original text for each text target
    if (this.hasTextTarget) {
      this.originalText = this.textTargets.map(target => target.textContent.trim());
    }

    // Bind event handlers
    this.onStart = this.onStart.bind(this);
    this.onFetchResponse = this.onFetchResponse.bind(this);
    this.onSubmitEnd = this.onSubmitEnd.bind(this);
    this.onDismissed = this.onDismissed.bind(this);

    // A step the submission handed off to — a PIN, a passkey — can be dismissed
    // instead of answered. Nothing comes back then, so without this the button
    // spins for a request that is never going to finish.
    document.addEventListener("waas:sheet-dismissed", this.onDismissed);

    // Listen for Turbo form events
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("turbo:submit-start", this.onStart);
      this.formTarget.addEventListener("turbo:before-fetch-response", this.onFetchResponse);
      this.formTarget.addEventListener("turbo:submit-end", this.onSubmitEnd);
    }
  }

  disconnect() {
    document.removeEventListener("waas:sheet-dismissed", this.onDismissed);

    // Clean up event listeners
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("turbo:submit-start", this.onStart);
      this.formTarget.removeEventListener("turbo:before-fetch-response", this.onFetchResponse);
      this.formTarget.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    }
  }

  onStart() {
    this.lock();
  }

  onFetchResponse(e) {
    // Unlock if response is not OK (>=400)
    const ok = e.detail.fetchResponse.response.ok;
    if (!ok) this.unlock();
    // If OK, the frame/page will replace; no need to unlock
  }

  onSubmitEnd(e) {
    // Unlock on any submission failure
    if (e.detail.success === false) this.unlock();
  }

  onDismissed() {
    if (!this.locked) return;
    this.unlock();
    if (this.hasCancelledMessageValue && this.cancelledMessageValue) {
      toast(this.cancelledMessageValue, "info");
    }
  }

  // Takes the screen out of use without claiming anything is being processed:
  // no spinner, no changed label. Leaving for another screen uses this.
  freeze() {
    this.submitTargets.forEach(btn => {
      btn.setAttribute("disabled", "disabled");
      btn.classList.add("cursor-not-allowed", "opacity-50");
    });

    this.lockableTargets.forEach(el => {
      el.setAttribute("aria-disabled", "true");
      el.classList.add("pointer-events-none", "opacity-60");
      el.querySelectorAll("[data-select-target='dropdown']").forEach(list => list.classList.add("hidden"));
      this.fieldsIn(el).forEach(field => this.freezeField(field));
    });
  }

  fieldsIn(el) {
    const fields = Array.from(el.querySelectorAll("input, textarea, select, button"));
    if (el.matches("input, textarea, select, button")) fields.unshift(el);
    return fields;
  }

  // Blocking the pointer is not enough for a field the caret is already in, and
  // readonly rather than disabled because a disabled field leaves the form data.
  freezeField(field) {
    if (field.matches("input, textarea")) {
      if (field.readOnly) return;
      field.readOnly = true;
      field.dataset.formSubmitFroze = "readonly";
      field.blur();
      return;
    }

    if (field.disabled) return;
    field.disabled = true;
    field.dataset.formSubmitFroze = "disabled";
  }

  thawField(field) {
    if (field.dataset.formSubmitFroze === "readonly") field.readOnly = false;
    if (field.dataset.formSubmitFroze === "disabled") field.disabled = false;
    delete field.dataset.formSubmitFroze;
  }

  lock() {
    this.locked = true;
    this.freeze();

    this.submitTargets.forEach(btn => {
      btn.setAttribute("aria-busy", "true");
      btn.classList.add("hover:bg-black");
      btn.style.filter = "brightness(95%)";
    });

    if (this.hasSpinnerTarget) {
      this.spinnerTargets.forEach(spinner => spinner.classList.remove("hidden"));
    }

    if (this.hasTextTarget && this.hasTextValue) {
      this.textTargets.forEach(target => {
        target.textContent = this.textValue || "Please wait";
      });
    }
  }

  unlock() {
    this.locked = false;
    this.submitTargets.forEach(btn => {
      btn.removeAttribute("disabled");
      btn.removeAttribute("aria-busy");
      btn.classList.remove("cursor-not-allowed", "opacity-50", "hover:bg-black");
      btn.style.filter = "";
    });

    // Hide spinner
    if (this.hasSpinnerTarget) {
      this.spinnerTargets.forEach(spinner => {
        spinner.classList.add("hidden");
      });
    }

    // Restore original button text
    if (this.hasTextTarget && this.originalText) {
      this.textTargets.forEach((target, index) => {
        target.textContent = this.originalText[index] || "";
      });
    }

    this.lockableTargets.forEach(el => {
      el.removeAttribute("aria-disabled");
      el.classList.remove("pointer-events-none", "opacity-60");
      this.fieldsIn(el).forEach(field => this.thawField(field));
    });
  }
}